"use client";

import React, { useState, type CSSProperties } from "react";
import Image from "next/image";
import { isStudioPendingImageSrc } from "@/utils/studioImageStyles";

interface ImageLightboxProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  style?: CSSProperties;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  src,
  alt,
  width,
  height,
  className = "",
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const openLightbox = () => setIsOpen(true);
  const closeLightbox = () => setIsOpen(false);

  if (isStudioPendingImageSrc(src)) {
    return (
      <div className="relative inline-block max-w-full" style={style}>
        {/* Pending local file preview — not an uploaded asset. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={`${className} opacity-50 grayscale`}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/45">
          <p className="px-3 text-center font-monument text-sm font-semibold tracking-wide text-white">
            Not uploaded yet
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={`cursor-pointer ${className}`}
        style={style}
        onClick={openLightbox}
      />

      {isOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close image preview"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
          onClick={closeLightbox}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
              e.preventDefault();
              closeLightbox();
            }
          }}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              className="absolute -top-10 right-0 p-2 text-white text-2xl"
              onClick={closeLightbox}
            >
              ✕
            </button>
            <Image
              src={src}
              alt={alt}
              width={width * 1.5}
              height={height * 1.5}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ImageLightbox;
